<script setup>
import { ref, defineEmits, defineProps, onMounted, onUnmounted, computed, defineAsyncComponent, nextTick} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore} from 'vuex';
import { MATCH_STATUS } from '../../utils/matchStatus';
/* Components */
const UpdatePidSection = defineAsyncComponent( () => import('./ProductStatusModal/UpdatePidSection.vue'));
const MatchSection = defineAsyncComponent( () => import('./ProductStatusModal/MatchSection.vue'));
const ProductAssociationSection = defineAsyncComponent( () => import('./ProductStatusModal/ProductAssociationSection.vue'));
/* Emits */
const emits = defineEmits(['closeModal', 'PidUpdated', 'newNoMatch', 'associated', 'stale']);
/* Props */
const props = defineProps({
    product:{required:true},
    productStatus:{required:true},
    // Il periodo e' chiuso da un report: si guarda, non si cambia.
    periodReported:{required:false, type: Boolean, default: false},
});
/* Data */
// const sections = ['match', 'noMatch', 'updatePid'];
const activeSection = ref('updatePid');
const { t } = useI18n();
const store = useStore();
const dialog = ref(null);
// L'elemento che aveva il fuoco quando il modale si e' aperto: alla chiusura ci
// deve tornare, o chi naviga da tastiera si ritrova all'inizio della pagina.
const opener = typeof document !== 'undefined' ? document.activeElement : null;
// Vero mentre una scelta e' in volo: chiudere adesso non annullerebbe la
// richiesta, la lascerebbe arrivare senza che nessuno aggiorni la riga.
const busy = ref(false);
/* Computed */
const modalTitle = computed(() => 
{
    switch(activeSection.value)
    {
        case 'match':
            return 'Match';
        case 'association':
            // Il titolo segue lo stato, non la sezione: sono due situazioni
            // diverse anche se si risolvono nello stesso modo.
            if(props.productStatus === MATCH_STATUS.ACTION_REQUIRED) return t('associationSection.titleChoose', store.state.locale);
            if(props.productStatus === MATCH_STATUS.MANUAL_MATCH) return t('associationSection.titleManualMatch', store.state.locale);

            return t('associationSection.titleNoMatch', store.state.locale);
        case 'updatePid':
            return t('updatePidModal.title', store.state.locale);
        default:
            return props.product.name
    }
});
/* Methods */
const init = () =>
{
    switch(props.productStatus)
    {
        case MATCH_STATUS.MATCH:
            return activeSection.value = 'match';
        // Due stati, una domanda: quale prodotto e' questo. In `action_required`
        // la ricerca automatica ha trovato dei candidati fra cui non ha saputo
        // scegliere, in `no_match` non ha trovato niente — e in entrambi i casi
        // serve poter cercare, perche' fra i candidati proposti quello giusto puo'
        // non esserci.
        case MATCH_STATUS.NO_MATCH:
        case MATCH_STATUS.ACTION_REQUIRED:
            return activeSection.value = 'association';
        // Un match manuale si rivede dove si e' fatto: la stessa ricerca, con
        // sopra il prodotto a cui si e' gia' associati. Finiva sul modale del
        // PID perche' li' viveva l'unico pulsante per dichiarare un no-match —
        // che ora sta nella sezione dell'associazione, dove la domanda e' quella
        // giusta: e' ancora questo il prodotto?
        case MATCH_STATUS.MANUAL_MATCH:
            return activeSection.value = 'association';
        default:
            return activeSection.value = 'updatePid';
    }
}
/**
 * Il fuoco resta dentro il modale finche' e' aperto: altrimenti si continua a
 * tabulare sulla pagina sotto, che nel frattempo non e' raggiungibile col mouse.
 */
const focusables = () => Array.from(
    dialog.value?.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])') ?? []
);

const onKeydown = (e) =>
{
    if(e.key === 'Escape' && !busy.value) return close();

    if(e.key !== 'Tab') return;

    const items = focusables();

    if(!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    // Il fuoco e' finito fuori — tipicamente sul body, perche' l'elemento che lo
    // aveva e' stato rimosso dal DOM. Riportarlo dentro: senza, il Tab
    // successivo se ne andrebbe sulla pagina sotto, che non e' nemmeno
    // raggiungibile col mouse.
    if(!dialog.value?.contains(document.activeElement))
    {
        e.preventDefault();

        return (e.shiftKey ? last : first).focus();
    }

    if(e.shiftKey && document.activeElement === first)
    {
        e.preventDefault();
        last.focus();
    }
    else if(!e.shiftKey && document.activeElement === last)
    {
        e.preventDefault();
        first.focus();
    }
};

const close = () =>
{
    if(busy.value) return;

    emits('closeModal');
};

const pidUpdated = (newPid) => emits('PidUpdated', newPid);
const newNoMatch = (noMatchGuid) => emits('newNoMatch', noMatchGuid);
/* Hooks */
// change activeSection based on props.productStatus
init();
onMounted( async () =>
{
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', onKeydown);

    await nextTick();
    focusables()[0]?.focus();
});
onUnmounted( () =>
{
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', onKeydown);
    opener?.focus?.();
});
</script>
<template>
    <div
        @click.prevent="close"
        class="fixed inset-0 p-6 bg-gray-1000 bg-opacity-90 z-30 overscroll-y-contain overflow-y-auto">
        <div class="flex flex-col w-full h-full items-center justify-center">
            <div
                @click.stop
                ref="dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="product-status-modal-title"
                style="max-height: 85vh"
                class="bg-white dark:bg-gray-900 border dark:border-gray-700 rounded p-5 flex flex-col space-y-5 overflow-hidden w-full md:w-2/3 lg:w-1/2 2xl:w-1/3 dark:text-gray-400">
                <div class="flex items-center justify-between">
                    <h2 id="product-status-modal-title" class="text-xl font-bold flex items-center space-x-1">
                        {{ modalTitle }}
                    </h2>
                    <button
                        :title="t('actions.close', store.state.locale)"
                        :disabled="busy"
                        @click.prevent="close"
                        class="bg-transparent min-w-min flex items-center justify-center transform hover:rotate-90 duration-300">
                            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 64 64" class="w-4 fill-current text-gray-800 dark:text-gray-400">
                                <path d="M34.5,32L62.2,4.2c0.7-0.7,0.7-1.8,0-2.5c-0.7-0.7-1.8-0.7-2.5,0L32,29.5L4.2,1.8c-0.7-0.7-1.8-0.7-2.5,0
                                    c-0.7,0.7-0.7,1.8,0,2.5L29.5,32L1.8,59.8c-0.7,0.7-0.7,1.8,0,2.5c0.3,0.3,0.8,0.5,1.2,0.5s0.9-0.2,1.2-0.5L32,34.5l27.7,27.8
                                    c0.3,0.3,0.8,0.5,1.2,0.5c0.4,0,0.9-0.2,1.2-0.5c0.7-0.7,0.7-1.8,0-2.5L34.5,32z"/>
                            </svg>
                    </button>
                </div>
                <!-- Il corpo prende lo spazio che resta e non oltre: e' cosi' che
                     l'intestazione con il titolo e la chiusura, e la ricerca nella
                     sezione, restano visibili mentre i risultati scorrono. -->
                <div class="flex-1 min-h-0 flex flex-col">
                <template v-if="activeSection == 'match'">
                    <MatchSection 
                        :product="props.product"
                    />
                </template>
                <template v-else-if="activeSection == 'association'">
                    <ProductAssociationSection
                        :product="props.product"
                        :productStatus="props.productStatus"
                        :periodReported="props.periodReported"
                        @associated="reference => emits('associated', reference)"
                        @busy="value => busy = value"
                        @newNoMatch="newNoMatch"
                        @stale="emits('stale')"
                        @close="close"
                    />
                </template>
                <template v-else>
                    <UpdatePidSection 
                        :product="props.product"
                        :productStatus="props.productStatus"
                        :periodReported="props.periodReported"
                        @PidUpdated="pidUpdated"
                        @busy="value => busy = value"
                        @newNoMatch="newNoMatch"
                    />
                </template>
                </div>
            </div>
        </div>
    </div>
</template>
